import { z } from 'zod';

/** Models staff can pick for reading service documents. */
export const SERVICE_HISTORY_MODELS = [
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview — Most powerful' },
  { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash — Fast, latest' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro — Best reasoning' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash — Fast + capable' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite — Cheapest' },
] as const;

export type ServiceHistoryModel = (typeof SERVICE_HISTORY_MODELS)[number]['value'];

export const SERVICE_HISTORY_DEFAULT_MODEL: ServiceHistoryModel = 'gemini-2.5-pro';

export const SERVICE_HISTORY_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export const SERVICE_HISTORY_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const SERVICE_HISTORY_MAX_FILES = 30;

const modelValues = SERVICE_HISTORY_MODELS.map((m) => m.value) as [
  ServiceHistoryModel,
  ...ServiceHistoryModel[],
];
export const serviceHistoryModelSchema = z.enum(modelValues);

export const SERVICE_ENTRY_TYPES = ['service', 'mot', 'repair', 'other'] as const;

/** One visit the AI found. Every entry names the file (and page) it came from. */
export const serviceEntrySchema = z.object({
  entry_type: z.enum(SERVICE_ENTRY_TYPES),
  date: z.string().nullable(),
  mileage: z.number().nullable(),
  garage: z.string().nullable(),
  work_done: z.string(),
  stamped: z.boolean().nullable(),
  source_file: z.string(),
  source_page: z.number().nullable(),
});

export const unreadableItemSchema = z.object({
  source_file: z.string(),
  description: z.string(),
});

/** Raw shape the model must return. */
export const serviceHistoryAiResultSchema = z.object({
  registrations_found: z.array(z.string()),
  make: z.string().nullable(),
  model: z.string().nullable(),
  summary: z.string(),
  services: z.array(serviceEntrySchema),
  unreadable: z.array(unreadableItemSchema),
});

export const serviceGapSchema = z.object({
  from_date: z.string(),
  to_date: z.string(),
  months: z.number(),
  miles: z.number().nullable(),
});

/** Saved key facts: the AI result plus figures we compute ourselves (services only, not MOTs). */
/** Saved entry: the file it came from is resolved to a stored document id. */
const savedEntrySchema = serviceEntrySchema.extend({ source_document_id: z.string().nullable() });
const savedUnreadableSchema = unreadableItemSchema.extend({
  source_document_id: z.string().nullable(),
});

export const serviceKeyFactsSchema = serviceHistoryAiResultSchema.omit({ summary: true }).extend({
  services: z.array(savedEntrySchema),
  unreadable: z.array(savedUnreadableSchema),
  last_service: z.object({ date: z.string(), mileage: z.number().nullable() }).nullable(),
  total_services: z.number(),
  gaps: z.array(serviceGapSchema),
});

export type ServiceEntry = z.infer<typeof serviceEntrySchema>;
export type ServiceHistoryAiResult = z.infer<typeof serviceHistoryAiResultSchema>;
export type ServiceGap = z.infer<typeof serviceGapSchema>;
export type ServiceKeyFacts = z.infer<typeof serviceKeyFactsSchema>;
export type SavedServiceEntry = z.infer<typeof savedEntrySchema>;

export const saveServiceDraftSchema = z.object({
  registration: z.string().trim().min(2).max(20),
});
export type SaveServiceDraftDto = z.infer<typeof saveServiceDraftSchema>;

export const regenerateServiceSummarySchema = z.object({
  model: serviceHistoryModelSchema,
});
export type RegenerateServiceSummaryDto = z.infer<typeof regenerateServiceSummarySchema>;

export const createServiceHistoryTokenSchema = z.object({
  name: z.string().trim().min(1).max(255),
});
export type CreateServiceHistoryTokenDto = z.infer<typeof createServiceHistoryTokenSchema>;

export type ServiceSummaryStatus = 'draft' | 'ready' | 'failed';

export interface ServiceDocument {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  uploaded_at: string;
  deleted_at: string | null;
}

export interface ServiceSummary {
  id: string;
  status: ServiceSummaryStatus;
  model_name: string;
  summary: string | null;
  key_facts: ServiceKeyFacts | null;
  source_document_ids: string[];
  error: string | null;
  is_current: boolean;
  input_tokens: number | null;
  output_tokens: number | null;
  generated_by: string | null;
  generated_at: string;
  /** The model's original text, kept once staff edit the summary. */
  ai_summary: string | null;
  edited_by: string | null;
  edited_at: string | null;
}

export const editServiceSummarySchema = z.object({
  summary: z.string().trim().min(10).max(5000),
});
export type EditServiceSummaryDto = z.infer<typeof editServiceSummarySchema>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #1a2b3c');

export const SERVICE_PDF_FONTS = ['Helvetica', 'Times-Roman', 'Courier', 'custom'] as const;
export const SERVICE_PDF_HEADER_STYLES = ['banner', 'line', 'minimal'] as const;

/**
 * Look of the customer-facing PDF. Asset fields hold storage paths set by the
 * upload endpoint, never URLs typed by staff.
 */
export const servicePdfThemeSchema = z.object({
  brand_name: z.string().max(120).default(''),
  title: z.string().max(80).default('Service history'),
  subtitle: z.string().max(160).default(''),
  header_style: z.enum(SERVICE_PDF_HEADER_STYLES).default('banner'),
  primary_color: hexColor.default('#1f2937'),
  accent_color: hexColor.default('#2563eb'),
  text_color: hexColor.default('#111827'),
  table_header_color: hexColor.default('#f3f4f6'),
  stripe_rows: z.boolean().default(true),
  font: z.enum(SERVICE_PDF_FONTS).default('Helvetica'),
  logo_path: z.string().max(500).nullable().default(null),
  font_regular_path: z.string().max(500).nullable().default(null),
  font_bold_path: z.string().max(500).nullable().default(null),
  show_gaps: z.boolean().default(true),
  show_unreadable: z.boolean().default(true),
  footer_text: z.string().max(400).default(''),
  disclaimer: z
    .string()
    .max(600)
    .default(
      'Read from the service documents on file. Please check the original records for full details.',
    ),
});
export type ServicePdfTheme = z.infer<typeof servicePdfThemeSchema>;

export const DEFAULT_SERVICE_PDF_THEME: ServicePdfTheme = servicePdfThemeSchema.parse({});

export const SERVICE_PDF_ASSET_KINDS = ['logo', 'font_regular', 'font_bold'] as const;
export type ServicePdfAssetKind = (typeof SERVICE_PDF_ASSET_KINDS)[number];

export const updateServiceHistorySettingsSchema = z.object({
  system_instruction: z.string().trim().min(50).max(20000).optional(),
  pdf_theme: servicePdfThemeSchema.optional(),
});
export type UpdateServiceHistorySettingsDto = z.infer<typeof updateServiceHistorySettingsSchema>;

/** Render a preview with an unsaved theme, on sample data or a chosen car. */
export const previewServicePdfSchema = z.object({
  pdf_theme: servicePdfThemeSchema,
  vehicle_id: z.string().uuid().optional(),
});
export type PreviewServicePdfDto = z.infer<typeof previewServicePdfSchema>;

/** The editable AI instructions and PDF design shown on the Service History page. */
export interface ServiceHistorySettings {
  system_instruction: string;
  /** The built-in text, for "Reset to default". */
  default_instruction: string;
  pdf_theme: ServicePdfTheme;
  updated_by: string | null;
  updated_at: string | null;
}

/** Result of "Read files": a draft for staff to check before saving. */
export interface ServiceDraft {
  draft_id: string;
  /** Registration to pre-fill, or null when none or several were found. */
  suggested_registration: string | null;
  /** Shown when the files hold no registration or more than one. */
  registration_warning: string | null;
  /** Set when the suggested registration already has saved files. */
  existing_vehicle_id: string | null;
  summary: ServiceSummary;
  documents: ServiceDocument[];
}

export interface ServiceVehicleListItem {
  id: string;
  registration: string;
  display_registration: string;
  document_count: number;
  last_service: { date: string; mileage: number | null } | null;
  total_services: number | null;
  model_name: string | null;
  generated_at: string | null;
  status: ServiceSummaryStatus | null;
  /** A short piece of the summary around the search term. */
  match_snippet: string | null;
  updated_at: string;
}

export interface PaginatedServiceVehicles {
  data: ServiceVehicleListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ServiceVehicleDetail {
  id: string;
  registration: string;
  display_registration: string;
  created_at: string;
  updated_at: string;
  current: ServiceSummary | null;
  documents: ServiceDocument[];
  history: ServiceSummary[];
}

/**
 * A file the agent can send. `send_as` tells each channel how to send it:
 * WhatsApp only takes JPG/PNG up to 5 MB as images, so anything else goes as a
 * document.
 */
export interface ServiceAttachment {
  title: string;
  file_name: string;
  url: string;
  mime_type: string;
  send_as: 'image' | 'document';
}

/** Lookup response for custom tools. `attachments` holds the summary PDF. */
export type ServiceHistoryLookupResult =
  | {
      found: true;
      registration: string;
      summary: string;
      key_facts: ServiceKeyFacts;
      generated_at: string;
      attachments: ServiceAttachment[];
    }
  | { found: false; registration: string; message: string };

/** Original files for custom tools, at most SERVICE_HISTORY_MAX_SEND_FILES. */
export type ServiceFilesResult =
  | { found: true; registration: string; attachments: ServiceAttachment[]; total: number }
  | { found: false; registration: string; message: string };

export const SERVICE_HISTORY_MAX_SEND_FILES = 10;

export const sendServiceHistoryWhatsAppSchema = z.object({
  registration: z.string().trim().min(2).max(20),
  include: z.enum(['summary', 'files', 'both']).default('summary'),
  /** Added by the platform on internal custom tools. */
  callId: z.string().uuid(),
});
export type SendServiceHistoryWhatsAppDto = z.infer<typeof sendServiceHistoryWhatsAppSchema>;

export interface SendServiceHistoryWhatsAppResult {
  sent: number;
  failed: number;
  /** How it went out: free-form inside the 24h window, or the approved template. */
  method: 'session' | 'template' | null;
  /** Set when nothing could be sent, in words the agent can pass on. */
  reason: string | null;
}

export interface ServiceHistoryApiToken {
  id: string;
  name: string;
  token_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}
